import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Download, Trash2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const money = (n, c) => `${c === "NZD" ? "NZ$" : c === "AUD" ? "A$" : ""}${Math.round(Number(n) || 0).toLocaleString()}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function InvoiceCompare() {
  const [locals, setLocals] = useState(null);
  const [error, setError] = useState(false);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [equipment, setEquipment] = useState("40HC");
  const [file, setFile] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ledger, setLedger] = useState({ entries: [], overcharge_by_currency: {}, count: 0 });
  const fileRef = useRef(null);

  const loadLedger = () => api.get("/supply-chain/invoice-audits").then((r) => setLedger(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/supply-chain/agreed-locals").then((r) => {
      setLocals(r.data);
      const f = r.data.lanes[0];
      if (f) { setOrigin(f.origin_code); setDestination(f.destination_code); }
    }).catch(() => setError(true));
    loadLedger();
  }, []);

  const lanes = locals?.lanes || [];
  const origins = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => (seen.has(l.origin_code) ? false : seen.add(l.origin_code))).map((l) => ({ code: l.origin_code, name: l.origin_name }));
  }, [lanes]);
  const destinations = useMemo(() => {
    const seen = new Set();
    return lanes.filter((l) => l.origin_code === origin).filter((l) => (seen.has(l.destination_code) ? false : seen.add(l.destination_code))).map((l) => ({ code: l.destination_code, name: l.destination_name }));
  }, [lanes, origin]);

  const onOrigin = (v) => { setOrigin(v); const f = lanes.find((l) => l.origin_code === v); if (f) setDestination(f.destination_code); setResult(null); };
  const lane = lanes.find((l) => l.origin_code === origin && l.destination_code === destination);
  const sizeKey = equipment === "20GP" ? "v20" : "v40";
  const currency = lane?.currency || "AUD";

  const runCompare = async () => {
    if (!file) { toast.error("Choose an invoice file first"); return; }
    setComparing(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("origin_code", origin);
    fd.append("destination_code", destination);
    fd.append("equipment", equipment);
    try {
      const r = await api.post("/supply-chain/invoice-compare", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success("Invoice compared", { description: `${r.data.lines.length} charge lines analysed` });
    } catch (e) {
      toast.error("Comparison failed", { description: e.response?.data?.detail || "Please check the file format." });
    } finally { setComparing(false); }
  };

  const saveAudit = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post("/supply-chain/invoice-audits", {
        origin_code: result.origin_code, origin_name: result.origin_name,
        destination_code: result.destination_code, destination_name: result.destination_name,
        equipment: result.equipment, currency: result.currency, filename: result.filename,
        total_invoiced: result.total_invoiced, total_agreed: result.total_agreed,
        total_overcharge: result.total_overcharge, off_contract_count: result.off_contract_count, lines: result.lines,
      });
      toast.success("Audit recorded", { description: `${money(result.total_overcharge, result.currency)} overcharge captured` });
      await loadLedger();
    } catch (e) {
      toast.error("Could not record", { description: e.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  const removeAudit = async (id) => {
    try { await api.delete(`/supply-chain/invoice-audits/${id}`); await loadLedger(); toast.success("Audit removed"); }
    catch (e) { toast.error("Could not remove", { description: e.response?.data?.detail || "Please try again." }); }
  };

  const exportCsv = () => {
    const head = ["Recorded", "Lane", "Equip", "Ccy", "Invoice file", "Invoiced", "Agreed", "Overcharge", "Off-contract lines", "Recorded by"];
    const rows = ledger.entries.map((e) => [fmtDate(e.created_at), `${e.origin_code}→${e.destination_code}`, e.equipment, e.currency, e.filename, e.total_invoiced, e.total_agreed, e.total_overcharge, e.off_contract_count, e.user_name || e.user_email || ""]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "PACT-invoice-audit-ledger.csv";
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    toast.success("Ledger exported");
  };

  const statusPill = (s) => {
    if (s === "over") return <span className="font-mono-plex text-[9px] tracking-widest uppercase text-red-600 border border-red-300 bg-red-50 rounded px-1.5 py-0.5">Overcharge</span>;
    if (s === "under") return <span className="font-mono-plex text-[9px] tracking-widest uppercase text-emerald-600 border border-emerald-300 bg-emerald-50 rounded px-1.5 py-0.5">Under agreed</span>;
    if (s === "match") return <span className="font-mono-plex text-[9px] tracking-widest uppercase text-emerald-600 border border-emerald-300 rounded px-1.5 py-0.5">On rate</span>;
    if (s === "at_cost") return <span className="font-mono-plex text-[9px] tracking-widest uppercase text-muted-foreground border border-border rounded px-1.5 py-0.5">At cost</span>;
    return <span className="font-mono-plex text-[9px] tracking-widest uppercase text-amber-600 border border-amber-300 bg-amber-50 rounded px-1.5 py-0.5">Off contract</span>;
  };

  if (error) return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="invcompare-error">Unable to load agreed destination locals.</div>;
  if (!locals) return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse" data-testid="invcompare-loading">Loading…</div>;

  return (
    <div data-testid="invoice-compare">
      <p className="text-sm text-muted-foreground max-w-3xl mb-6">
        Upload a destination-locals invoice and compare every charge line against the agreed destination locals for the selected lane. Charges are auto-matched by keyword; anything not on the agreed schedule is flagged. AU lanes in AUD, NZ lanes in NZD.
      </p>

      {/* Selectors + upload */}
      <div className="border border-border bg-white p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 items-end" data-testid="invcompare-lookup">
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Origin</label>
          <Select value={origin} onValueChange={onOrigin}>
            <SelectTrigger data-testid="invcompare-origin" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{origins.map((o) => <SelectItem key={o.code} value={o.code}>{o.name} ({o.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Destination</label>
          <Select value={destination} onValueChange={(v) => { setDestination(v); setResult(null); }}>
            <SelectTrigger data-testid="invcompare-destination" className="w-full bg-white border border-border rounded-md"><SelectValue /></SelectTrigger>
            <SelectContent>{destinations.map((d) => <SelectItem key={d.code} value={d.code}>{d.name} ({d.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Equipment</label>
          <div className="inline-flex border border-border rounded-md overflow-hidden w-full" data-testid="invcompare-equipment-toggle">
            {["20GP", "40HC"].map((eq) => (
              <button key={eq} onClick={() => { setEquipment(eq); setResult(null); }} data-testid={`invcompare-equipment-${eq}`}
                className={`flex-1 px-3 py-2 text-xs font-mono-plex tracking-widest uppercase transition-colors ${equipment === eq ? "bg-[#F59E0B] text-white" : "bg-white text-muted-foreground hover:text-foreground"}`}>
                {eq}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block font-mono-plex text-[11px] tracking-widest uppercase text-muted-foreground mb-2">Invoice file (.xlsx / .csv)</label>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="invcompare-file" className="hidden" />
          <button onClick={() => fileRef.current?.click()} data-testid="invcompare-choose-file" className="w-full inline-flex items-center gap-2 border border-border rounded-md px-3 py-2 text-xs text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors truncate">
            <FileSpreadsheet className="h-4 w-4 shrink-0" /> <span className="truncate">{file ? file.name : "Choose file…"}</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <button onClick={runCompare} disabled={comparing || !file} data-testid="invcompare-run"
          className="inline-flex items-center gap-2 bg-[#0072B8] text-white text-xs font-mono-plex tracking-widest uppercase px-5 py-2.5 rounded-md hover:bg-[#005a92] transition-colors disabled:opacity-50">
          <Upload className="h-3.5 w-3.5" /> {comparing ? "Comparing…" : "Compare invoice"}
        </button>
        {lane && <span className="text-xs text-muted-foreground">Comparing against agreed {currency} locals · {lane.origin_name} → {lane.destination_name} · {equipment}</span>}
      </div>

      {/* Agreed schedule reference */}
      {lane && !result && (
        <div className="border border-border bg-white overflow-x-auto mb-8" data-testid="invcompare-agreed">
          <div className="px-5 py-3.5 border-b border-border bg-slate-50/70 font-mono-plex text-[11px] tracking-widest uppercase font-semibold">Agreed destination locals · {equipment} · {currency}</div>
          <table className="w-full text-sm">
            <tbody>
              {lane.charges.map((ch, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-5 py-2.5 text-foreground/90">{ch.label}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-muted-foreground">{typeof ch[sizeKey] === "number" ? money(ch[sizeKey], currency) : (ch[sizeKey] || "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Comparison result */}
      {result && (
        <div data-testid="invcompare-result">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="border border-border bg-white p-5"><div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Total invoiced</div><div className="font-display text-2xl tabular-nums">{money(result.total_invoiced, result.currency)}</div></div>
            <div className="border border-border bg-white p-5"><div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Agreed (matched lines)</div><div className="font-display text-2xl tabular-nums">{money(result.total_agreed, result.currency)}</div></div>
            <div className={`border-2 p-5 ${result.total_overcharge > 0 ? "border-red-400 bg-red-50/50" : "border-emerald-400 bg-emerald-50/50"}`} data-testid="invcompare-overcharge">
              <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Overcharge identified</div>
              <div className={`font-display text-3xl tabular-nums ${result.total_overcharge > 0 ? "text-red-600" : "text-emerald-600"}`} data-testid="invcompare-overcharge-value">{money(result.total_overcharge, result.currency)}</div>
            </div>
          </div>

          <div className="border border-border bg-white overflow-x-auto mb-4" data-testid="invcompare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-slate-50/70">
                  {["Invoice charge line", "Matched to", "Invoiced", "Agreed", "Variance", "Status"].map((h, i) => (
                    <th key={i} className={`px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold ${i >= 2 && i <= 4 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.lines.map((l, i) => (
                  <tr key={i} className={`border-t border-slate-100 ${l.status === "over" ? "bg-red-50/40" : l.status === "off_contract" ? "bg-amber-50/30" : ""}`} data-testid={`invcompare-row-${i}`}>
                    <td className="px-4 py-2.5 text-foreground/90">{l.invoice_label}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.matched_label || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{money(l.invoiced, result.currency)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{typeof l.agreed === "number" ? money(l.agreed, result.currency) : (l.agreed || "—")}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${l.variance > 0 ? "text-red-600" : l.variance < 0 ? "text-emerald-600" : "text-muted-foreground"}`}>{l.variance == null ? "—" : `${l.variance > 0 ? "+" : ""}${money(l.variance, result.currency)}`}</td>
                    <td className="px-4 py-2.5">{statusPill(l.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.missing.length > 0 && (
            <div className="border border-border bg-white p-4 mb-6 text-xs text-muted-foreground" data-testid="invcompare-missing">
              <span className="font-semibold text-foreground">Agreed lines not on this invoice:</span> {result.missing.map((m) => `${m.label} (${money(m.agreed, result.currency)})`).join(" · ")}
            </div>
          )}

          <button onClick={saveAudit} disabled={saving} data-testid="invcompare-record"
            className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-mono-plex tracking-widest uppercase px-4 py-2.5 rounded-md hover:bg-[#d97f06] transition-colors disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {saving ? "Recording…" : "Record this audit"}
          </button>
        </div>
      )}

      {/* Ledger */}
      <div className="mt-12 mb-2 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Invoice audit ledger</h3>
          <p className="text-xs text-muted-foreground mt-1">Program-wide · {ledger.count} {ledger.count === 1 ? "audit" : "audits"} recorded</p>
        </div>
        <button onClick={exportCsv} disabled={!ledger.entries.length} data-testid="invcompare-export"
          className="inline-flex items-center gap-2 border border-border rounded-md px-3 py-1.5 text-[11px] font-mono-plex tracking-widest uppercase text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground transition-colors disabled:opacity-40">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {Object.keys(ledger.overcharge_by_currency).length > 0 && (
        <div className="border-2 border-red-400/40 bg-red-50/40 p-5 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="invcompare-total-captured">
          <div className="flex items-center gap-2 text-red-700"><ShieldCheck className="h-4 w-4" /><span className="font-mono-plex text-[11px] tracking-widest uppercase">Total overcharge captured to date</span></div>
          <div className="flex gap-6">
            {Object.entries(ledger.overcharge_by_currency).map(([c, v]) => (
              <div key={c} className="font-display text-2xl tracking-tight text-red-700 tabular-nums">{money(v, c)}</div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-border bg-white overflow-x-auto" data-testid="invcompare-ledger">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-slate-50/70">
              {["Recorded", "Lane", "Equip", "File", "Invoiced", "Overcharge", "Off-contract", "By", ""].map((h, i) => (
                <th key={i} className="px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.entries.map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid={`invcompare-ledger-row-${e.id}`}>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.created_at)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">{e.origin_code} → {e.destination_code}</td>
                <td className="px-4 py-2.5 text-xs">{e.equipment}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[160px] truncate">{e.filename}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{money(e.total_invoiced, e.currency)}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${e.total_overcharge > 0 ? "text-red-600" : "text-emerald-600"}`}>{money(e.total_overcharge, e.currency)}</td>
                <td className="px-4 py-2.5 text-center tabular-nums">{e.off_contract_count}</td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{e.user_name || e.user_email}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => removeAudit(e.id)} data-testid={`invcompare-ledger-delete-${e.id}`} className="text-muted-foreground/60 hover:text-red-600 transition-colors" aria-label="Remove audit"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
            {!ledger.entries.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">No audits recorded yet. Upload an invoice above, compare, then click “Record this audit”.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
